import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../../../lib/api";
import { useAuth } from "../../../../providers/AuthProvider";
import { buildCategoryTree, type CategoryFlat, type CategoryTreeNode } from "../utils/buildCategoryTree";

export type { CategoryFlat, CategoryTreeNode };

export function useCategories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState<Array<CategoryFlat & { product_count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!token) {
      setCategories([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.categories.list({ token });
      const next = res.data.map((c) => ({
        id: String(c.id),
        name: c.name,
        parent_id: c.parent_id ? String(c.parent_id) : null,
        product_count: Number(c.product_count ?? 0),
      }));
      setCategories(next);
    } catch (e) {
      const err = e as Error;
      setError(err.message || "Failed to load categories");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const categoriesTree = useMemo(() => buildCategoryTree(categories), [categories]);

  const hasChildren = useCallback(
    (id: string) => {
      return categories.some((c) => c.parent_id === id);
    },
    [categories],
  );

  const createCategory = useCallback(
    async ({ name, parent_id }: { name: string; parent_id: string | null }) => {
      if (!token) throw new Error("Not authenticated");
      const res = await api.categories.create({ token, payload: { name, parent_id } });
      setCategories((prev) => [
        ...prev,
        {
          id: String(res.id),
          name: res.name,
          parent_id: res.parent_id ? String(res.parent_id) : null,
        },
      ]);
    },
    [token],
  );

  const updateCategory = useCallback(
    async (id: string, payload: { name?: string; parent_id?: string | null }) => {
      if (!token) throw new Error("Not authenticated");
      const res = await api.categories.update({
        token,
        id,
        payload: {
          name: payload.name ?? null,
          parent_id: payload.parent_id === undefined ? undefined : payload.parent_id,
        },
      });
      setCategories((prev) =>
        prev.map((c) =>
          c.id === String(id)
            ? {
                id: String(res.id),
                name: res.name,
                parent_id: res.parent_id ? String(res.parent_id) : null,
                product_count: c.product_count,
              }
            : c,
        ),
      );
    },
    [token],
  );

  const deleteCategory = useCallback(
    async (id: string) => {
      if (!token) throw new Error("Not authenticated");
      if (hasChildren(id)) {
        const err = new Error("Category has children") as ApiError & { code?: string };
        err.code = "HAS_CHILDREN";
        throw err;
      }
      await api.categories.remove({ token, id });
      setCategories((prev) => prev.filter((c) => c.id !== String(id)));
    },
    [token, hasChildren],
  );

  return {
    categories,
    categoriesTree,
    loading,
    error,
    reload,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}

